import React, { useEffect, useRef } from "react";
import Vex from "vexflow";
import { parseCell } from "@/lib/drumAudio";

const VF = Vex.Flow;
// Standard drum-kit notation positions on a percussion stave.
const PITCH = {
  CC: "a/5/x2", RD: "f/5/x2", RB: "f/5/d2", HH: "g/5/x2", HT: "e/5", MT: "d/5", FT: "a/4",
  SD: "c/5", RS: "c/5/x2", CP: "c/5/x2", CB: "e/5/t2", TM: "b/4/x2", BD: "f/4",
};
const UPPER = ["CC", "RD", "RB", "HH", "HT", "MT", "SD", "RS", "CP", "CB", "TM", "FT"];
const LOWER = ["BD"];
const STD = { 16: ["q", 0], 12: ["8", 1], 8: ["8", 0], 6: ["16", 1], 4: ["16", 0], 3: ["32", 1], 2: ["32", 0], 1: ["64", 0] };
const STD_LENS = [16, 12, 8, 6, 4, 3, 2, 1];

const split = (len) => {
  const out = [];
  let r = Math.round(len);
  while (r > 0) {
    const l = STD_LENS.find((x) => x <= r);
    out.push(l);
    r -= l;
  }
  return out;
};

// Turn one beat of one voice into display notes (durations in 1/64ths, triplets scaled by 3/2).
function beatNotes(m, voices, beatStart, spb0, den, swing) {
  const beatLen = 64 / den;
  let hits = [];
  for (let p = 0; p < spb0; p++) {
    const keys = [];
    const ghosts = [];
    let accent = false;
    let open = false;
    voices.forEach((v) => {
      const c = parseCell((m.lanes[v] || "")[beatStart + p]);
      if (c.hit) {
        if (c.ghost) ghosts.push(keys.length);
        keys.push(PITCH[v]);
        if (c.accent && !(v === "HH" && c.open)) accent = true;
        if (v === "HH" && c.open) open = true;
      }
    });
    if (keys.length) hits.push({ p, keys, ghosts, accent, open, keyVoices: voices.filter((v) => parseCell((m.lanes[v] || "")[beatStart + p]).hit) });
  }
  // Simplify sextuplet beats that only use 8th or 8th-triplet positions.
  let spb = spb0;
  if (swing && spb0 === 3 && hits.every((h) => h.p !== 1)) { spb = 2; hits = hits.map((h) => ({ ...h, op: h.p, p: h.p / 2 })); }
  else if (spb0 === 6 && hits.every((h) => h.p % 3 === 0)) { spb = 2; hits = hits.map((h) => ({ ...h, op: h.p, p: h.p / 3 })); }
  else if (spb0 === 6 && hits.every((h) => h.p % 2 === 0)) { spb = 3; hits = hits.map((h) => ({ ...h, op: h.p, p: h.p / 2 })); }
  const stepLen = beatLen / spb;
  const tuplet = !Number.isInteger(stepLen) || spb % 3 === 0;
  const f = tuplet ? 1.5 : 1;
  if (!hits.length) return { notes: [{ rest: true, len: beatLen, step: beatStart }], tuplet: false };
  if (tuplet && hits.length === 1 && hits[0].p === 0) {
    return { notes: [{ ...hits[0], len: beatLen, step: beatStart }], tuplet: false };
  }
  const notes = [];
  if (hits[0].p > 0) split(hits[0].p * stepLen * f).forEach((l, i) => notes.push({ rest: true, len: l, step: beatStart + (i ? -1 : 0) }));
  hits.forEach((h, i) => {
    const end = i + 1 < hits.length ? hits[i + 1].p : spb;
    split((end - h.p) * stepLen * f).forEach((l, j) => notes.push(j === 0 ? { ...h, len: l, step: beatStart + (h.op != null ? h.op : h.p) } : { rest: true, len: l, step: -1 }));
  });
  return { notes, tuplet: tuplet ? { num: spb === 6 ? 6 : 3, occ: spb === 6 ? 4 : 2 } : false };
}

function makeNote(n, up) {
  const [dur, dots] = STD[n.len] || ["16", 0];
  const sn = new VF.StaveNote({
    clef: "percussion",
    keys: n.rest ? [up ? "c/5" : "e/4"] : n.keys,
    duration: dur + (n.rest ? "r" : ""),
    dots,
    stem_direction: up ? 1 : -1,
  });
  if (dots) VF.Dot.buildAndAttach([sn], { all: true });
  if (!n.rest) (n.ghosts || []).forEach((i) => {
    sn.addModifier(new VF.Parenthesis(VF.Modifier.Position.LEFT), i);
    sn.addModifier(new VF.Parenthesis(VF.Modifier.Position.RIGHT), i);
  });
  if (n.accent) sn.addModifier(new VF.Articulation("a>").setPosition(up ? VF.Modifier.Position.ABOVE : VF.Modifier.Position.BELOW), 0);
  if (n.open) sn.addModifier(new VF.Annotation("o").setVerticalJustification(VF.Annotation.VerticalJustify.TOP), 0);
  return sn;
}

const HANDS = new Set(UPPER);
function stickingFor(notes) {
  let k = 0;
  notes.forEach((n) => {
    if (n.rest || n.step < 0) return;
    const hands = n.keyVoices.filter((v) => HANDS.has(v)).length;
    if (hands >= 2) { n.stick = "B"; k++; return; }
    n.stick = k % 2 === 0 ? "R" : "L";
    k++;
  });
}

function drawMeasure(ctx, stave, m, beats, den, spb, opts = {}) {
  const voices = [];
  const beams = [];
  const tuplets = [];
  const marks = [];
  const sticks = [];
  [[UPPER, true], [LOWER, false]].forEach(([lanes, up]) => {
    const used = lanes.filter((v) => (m.lanes[v] || "").split("").some((c) => parseCell(c).hit));
    if (!used.length && !up) return;
    const tickables = [];
    const all = [];
    const perBeat = [];
    for (let b = 0; b < beats; b++) {
      const r = beatNotes(m, used, b * spb, spb, den, opts.swing);
      perBeat.push(r);
      all.push(...r.notes);
    }
    if (up) stickingFor(all);
    for (let b = 0; b < beats; b++) {
      const { notes, tuplet } = perBeat[b];
      const sns = notes.map((n) => {
        const sn = makeNote(n, up);
        if (n.step >= 0 && !n.rest) marks.push([n.step, sn]);
        if (up && !n.rest && n.step >= 0) {
          const key = `${opts.prefix}.${n.step}`;
          const custom = (opts.stickMap || {})[key];
          const label = custom ? (custom === "-" ? "" : custom) : opts.sticking || opts.edit ? n.stick || "" : "";
          sticks.push([label, sn, !!custom, key]);
        }
        if (n.step >= 0 && n.rest && n.step === b * spb) marks.push([n.step, sn]);
        return sn;
      });
      if (tuplet) tuplets.push(new VF.Tuplet(sns, { num_notes: tuplet.num, notes_occupied: tuplet.occ, ratioed: false, bracketed: true, location: up ? VF.Tuplet.LOCATION_TOP : VF.Tuplet.LOCATION_BOTTOM }));
      const beamable = sns.filter((s) => !s.isRest() && ["8", "16", "32", "64"].includes(s.getDuration()));
      if (beamable.length >= 2) beams.push(new VF.Beam(beamable));
      tickables.push(...sns);
    }
    const voice = new VF.Voice({ num_beats: beats, beat_value: den }).setMode(VF.Voice.Mode.SOFT);
    voice.addTickables(tickables);
    voices.push(voice);
  });
  new VF.Formatter().joinVoices(voices).formatToStave(voices, stave);
  voices.forEach((v) => v.draw(ctx, stave));
  beams.forEach((b) => b.setContext(ctx).draw());
  tuplets.forEach((t) => t.setContext(ctx).draw());
  const editables = [];
  if (sticks.length) {
    const y = stave.getYForLine(4) + 50;
    ctx.save();
    ctx.setFont("Arial", 12, "bold");
    sticks.forEach(([l, sn, custom, key]) => {
      const x = sn.getAbsoluteX() + 1;
      editables.push([key, x + 4, l || "-"]);
      if (!l && !opts.edit) return;
      ctx.setFillStyle(custom ? "#0b5cad" : opts.edit ? "#8a8a8a" : "#1a1a1a");
      ctx.fillText(l || "\u00b7", x, y);
    });
    ctx.restore();
  }
  return { marks, editables };
}

export const isFillMeasure = (sec, m) =>
  m.fill === true || (m.fill !== false && (
  /fill|solo|break/i.test(sec.name || "") ||
  ["HT", "MT", "FT"].reduce((a, v) => a + (m.lanes[v] || "").split("").filter((c) => parseCell(c).hit).length, 0) >= 2));

const STICK_SEQ = ["R", "L", "-"];

export const SheetMusic = ({ sections: allSections, spm, beats, den, width, zoom, apiRef, onSeek, swing = false, sticking = false, compact = false, stickMap, editSticking = false, onStickEdit, onPracticeFill }) => {
  const hostRef = useRef(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || !width) return undefined;
    host.innerHTML = "";
    const scale = zoom;
    const mwBase = Math.max(200, spm * 19);
    const avail = Math.floor((width - 8) / scale);
    const perRow = Math.max(1, Math.floor((avail - 40) / mwBase));
    const mw = Math.floor((avail - 40) / perRow);
    const stepPos = {};
    const marker = document.createElement("div");
    marker.className = "sheet-playhead";
    marker.setAttribute("data-testid", "sheet-playhead");

    if (swing) {
      const sw = document.createElement("div");
      sw.className = "pt-3 text-sm font-semibold text-[#1a1a1a]";
      sw.setAttribute("data-testid", "swing-marking");
      sw.innerHTML = 'Swing <span style="font-family:serif;font-size:16px">\u266B = \u2669<sup style="font-size:10px">3</sup>\u266A</span> <span style="font-weight:400;color:#555">(play 8ths as triplet shuffle)</span>';
      host.appendChild(sw);
    }
    const firstBySrc = (ms) => {
      const seen = {};
      ms.forEach((m, i) => { const k = m.src != null ? m.src : i; if (!(k in seen)) seen[k] = { ...m, src: k }; });
      return Object.keys(seen).map(Number).sort((a, b) => a - b).map((k) => seen[k]);
    };
    const sections = compact ? allSections.map((sec) => ({ ...sec, measures: firstBySrc(sec.measures) })) : allSections;
    sections.forEach((sec, si) => {
      const base = sec.base || sec.measures.length;
      const reps = compact ? sec.repeat || 1 : 1;
      const endLen = compact && reps > 1 ? sec.endLen || 0 : 0;
      const srcPos = {};
      const head = document.createElement("div");
      head.className = "font-heading text-sm uppercase tracking-widest text-[#9a5a00] mt-4 mb-1";
      head.textContent = (sec.name || `Section ${si + 1}`) + (reps > 1 ? `  \u00d7${reps}` : "");
      host.appendChild(head);
      for (let r = 0; r < sec.measures.length; r += perRow) {
        const rowMs = sec.measures.slice(r, r + perRow);
        const row = document.createElement("div");
        row.setAttribute("data-tab-row", "");
        row.className = "relative cursor-pointer";
        host.appendChild(row);
        const h = 170;
        const rmw = rowMs.length < perRow ? Math.min(Math.floor((avail - 40) / rowMs.length), Math.round(mw * 1.6)) : mw;
        const renderer = new VF.Renderer(row, VF.Renderer.Backends.SVG);
        renderer.resize(Math.ceil((40 + rmw * rowMs.length + 4) * scale), Math.ceil(h * scale));
        const ctx = renderer.getContext();
        ctx.scale(scale, scale);
        const svg = row.querySelector("svg");
        svg.style.color = "#e2e8f0";
        const rowSteps = [];
        const rowEdit = [];
        let x = 0;
        rowMs.forEach((m, mi) => {
          const first = mi === 0;
          const idx = r + mi;
          const w = rmw + (first ? 40 : 0);
          const stave = new VF.Stave(x, 40, w);
          if (first) stave.addClef("percussion");
          if (si === 0 && r === 0 && mi === 0) stave.addTimeSignature(`${beats}/${den}`);
          if (reps > 1 && idx === 0) stave.setBegBarType(VF.BarlineType.REPEAT_BEGIN);
          if (reps > 1 && idx === base - 1) stave.setEndBarType(VF.BarlineType.REPEAT_END);
          if (endLen) {
            const voltaFor = (start, label) => {
              const k = idx - start;
              if (k < 0 || k >= endLen) return;
              const t = endLen === 1 ? VF.VoltaType.BEGIN_END : k === 0 ? VF.VoltaType.BEGIN : k === endLen - 1 ? VF.VoltaType.END : VF.VoltaType.MID;
              stave.setVoltaType(t, label, -2);
            };
            voltaFor(base - endLen, "1.");
            voltaFor(base, "2.");
          }
          stave.setContext(ctx).draw();
          if (reps > 1 && idx === base - 1) {
            ctx.save();
            ctx.setFont("Arial", 13, "bold");
            ctx.fillText(`\u00d7${reps}`, x + w - 30, endLen ? 9 : 14);
            ctx.restore();
          }
          let marks = [];
          const n = m.steps || spm;
          const mspb = Math.max(1, Math.round(n / beats));
          if (onPracticeFill && isFillMeasure(sec, m)) {
            const btn = document.createElement("button");
            btn.type = "button";
            btn.className = "practice-fill-btn";
            btn.textContent = "\u25B6 Practice this fill";
            btn.title = "Loop this bar slowly with sticking, speeding up as you go";
            btn.setAttribute("data-testid", `practice-fill-${m.startStep}`);
            btn.style.left = `${(x + (first ? 48 : 8)) * scale}px`;
            const a = m.startStep;
            const name = sec.name;
            btn.addEventListener("click", (e) => { e.stopPropagation(); onPracticeFill(a, a + n - 1, name); });
            row.appendChild(btn);
          }
          try {
            const res = drawMeasure(ctx, stave, m, beats, den, mspb, {
              swing: swing && n === spm, sticking: sticking && isFillMeasure(sec, m),
              edit: editSticking, stickMap: sticking || editSticking ? stickMap : {}, prefix: `${si}.${m.src != null ? m.src : idx % base}`,
            });
            marks = res.marks;
            res.editables.forEach(([k, ex, cur]) => rowEdit.push([k, ex * scale, cur]));
          } catch (e) { marks = []; }
          const pts = marks.map(([s, sn]) => [s, sn.getAbsoluteX() + 5]).sort((a, b) => a[0] - b[0]);
          const startX = stave.getNoteStartX();
          const endX = stave.getX() + stave.getWidth();
          const known = [[0, startX], ...pts, [n, endX - 6]];
          for (let s = 0; s < n; s++) {
            let i = 0;
            while (i < known.length - 1 && known[i + 1][0] <= s) i++;
            const [s0, x0] = known[i];
            const [s1, x1] = known[Math.min(i + 1, known.length - 1)];
            const px = s1 === s0 ? x0 : x0 + ((s - s0) / (s1 - s0)) * (x1 - x0);
            const g = m.startStep + s;
            stepPos[g] = { row, x: px * scale };
            (srcPos[m.src] = srcPos[m.src] || [])[s] = stepPos[g];
            rowSteps.push([g, px * scale]);
          }
          x += w;
        });
        row.addEventListener("click", (e) => {
          const bx = e.clientX - row.getBoundingClientRect().left;
          if (editSticking) {
            let hit = null;
            rowEdit.forEach((p) => { if (Math.abs(p[1] - bx) < 16 * scale && (!hit || Math.abs(p[1] - bx) < Math.abs(hit[1] - bx))) hit = p; });
            if (hit && onStickEdit) onStickEdit(hit[0], STICK_SEQ[(STICK_SEQ.indexOf(hit[2]) + 1) % 3]);
            return;
          }
          let best = rowSteps[0];
          rowSteps.forEach((p) => { if (Math.abs(p[1] - bx) < Math.abs(best[1] - bx)) best = p; });
          if (best) onSeek(best[0]);
        });
      }
      if (compact) {
        allSections[si].measures.forEach((em) => {
          const pos = srcPos[em.src];
          if (pos) for (let k = 0; k < (em.steps || spm); k++) if (pos[k]) stepPos[em.startStep + k] = pos[k];
        });
      }
    });

    apiRef.current = {
      highlight: (n) => {
        const p = stepPos[n];
        if (!p) { marker.remove(); return null; }
        if (marker.parentNode !== p.row) p.row.appendChild(marker);
        marker.style.transform = `translateX(${p.x - 1.5}px)`;
        return marker;
      },
      pos: (n) => stepPos[n] || null,
      rows: () => Array.from(host.querySelectorAll("[data-tab-row]")),
    };
    return () => { apiRef.current = null; };
  }, [allSections, spm, beats, den, width, zoom, apiRef, onSeek, swing, sticking, compact, stickMap, editSticking, onStickEdit, onPracticeFill]);

  return <div ref={hostRef} className={`sheet-music overflow-x-auto${editSticking ? " cursor-cell" : ""}`} data-testid="sheet-music" data-compact={compact ? "1" : "0"} />;
};
